import React, { useCallback } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useTopicTrafficByKeyword } from '../Total/hooks/useTopicTrafficByKeyword';
import { useMainTimePeriodShortDisplayName, useMetricFormatter } from 'src/utils/Hooks';
import { TrafficSplineChartForTopics } from '../Total/TrafficSplineChartForTopics';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { TopicGroupInclusions } from './TopicGroupInclusions';
import { StandardTopicSettingsSidebar } from './Sidebars/StandardTopicSettingsSidebar';
import { useMainEntity } from 'src/utils/Hooks/reduxSelectors';
import { Topic, TopicGroup, TopicWithOwner } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import {
  getTopicOrTopicGroupType,
  isStandardTopicOrTopicGroup
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { DeleteTopicModal } from './modals/DeleteTopicModal';
import { TopicBuilderSidebarV2 } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/TopicBuilderSidebarV2';
import {
  StorageSource,
  useTopicBuilderActions
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import { TopicBuilderEditResults } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Visualizations/TopicBuilderEditResults';
import { CustomTopicGroupSettingsSidebar } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/Sidebars/CustomTopicGroupSettingsSidebar';
import { TopicTrafficByKeywords } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/TrafficGridTopics';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { RemoveTopicModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/modals/RemoveTopicModal';
import { TopicGroupInclusionProvider } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/TopicGroupInclusions/TopicGroupInclusionProvider';
import { EditGroupInclusionsModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/TopicGroupInclusions/EditGroupInclusionsModal';

/**
 * Settings page for topics
 */
export function TopicSettingsPage() {
  const formatMetric = useMetricFormatter();
  const { response, isLoading } = useTopicTrafficByKeyword({ sortField: 'totalTraffic', includeClassification: true });
  const periodName = useMainTimePeriodShortDisplayName();
  const mainEntity = useMainEntity() as unknown as TopicGroup | TopicWithOwner;
  const topicClassification = getTopicOrTopicGroupType(mainEntity.sharedWithEntityId, mainEntity.ownedByUserId);
  const isTopic = mainEntity?.type === 'topic';
  const isStandard = topicClassification === 'Standard';
  const isCustom = topicClassification === 'Custom';
  const isOwned = topicClassification === 'Owned';

  const { storage } = useTopicBuilderActions();

  /**
   * Save the topic to local storage for owned topics.
   * The TopicBuilderSidebar will auto populate with the saved topic.
   */
  React.useEffect(() => {
    if (isTopic && topicClassification !== 'Standard') {
      storage.save(mainEntity as Topic, StorageSource.EDIT);
    }
  }, [mainEntity, isTopic, topicClassification, storage]);

  const renderSidebar = useCallback(() => {
    if (isStandard) {
      return <StandardTopicSettingsSidebar />;
    } else if (isTopic && (isCustom || isOwned)) {
      return <TopicBuilderSidebarV2 source={StorageSource.EDIT} readOnly={!isOwned} editMode topic={mainEntity} />; // TODO: Fix type for mainEntity
    } else if (isCustom) {
      return <CustomTopicGroupSettingsSidebar />;
    } else {
      return null;
    }
  }, [mainEntity, isStandard, isTopic, isCustom, isOwned]);

  const renderTable = useCallback(() => {
    if (isStandard) {
      return <TopicTrafficByKeywords trafficType="totalTraffic" />;
    } else {
      return <TopicBuilderEditResults readOnly={!isOwned} />;
    }
  }, [isStandard, isOwned]);

  return (
    <>
      {/* Dialog root for add to topic group modal */}
      <BasePageContainer
        sx={{
          marginTop: '31px'
        }}
        sidebar={renderSidebar()}
        sidebarContainerSx={{
          marginTop: isStandardTopicOrTopicGroup(mainEntity.sharedWithEntityId) ? '46px' : '16px'
        }}
      >
        <Flex justifyContent="space-between" alignItems="center" marginBottom="8px">
          <Text variant="h4">Keywords</Text>
        </Flex>
        {!isLoading && (
          <Text variant="body1">
            This topic displays the total traffic for{' '}
            {formatMetric(response?.[0]?.records?.length ?? 0, METRICTYPE.VOLUME)} unique keywords during the{' '}
            {periodName} period.
          </Text>
        )}
        <Flex marginTop="32px" flexDirection="column" gap={BEACON_CHART_SPACING}>
          {renderTable()}
          <TrafficSplineChartForTopics trafficType="totalTraffic" />
          <TopicGroupInclusionProvider>
            <TopicGroupInclusions />
            <EditGroupInclusionsModal />
          </TopicGroupInclusionProvider>
        </Flex>
      </BasePageContainer>
      <DeleteTopicModal />
      <RemoveTopicModal />
    </>
  );
}
